import { Badge } from "@/components/Badge";
import Icon from "@/components/Icon/Icon";
import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { TextVariant } from "@/components/Typography/Text.variant";
import { CustomSaasType } from "@/constants/customSaas";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { Link } from "@tanstack/react-router";
import { APP_ID } from "../_$id/-constant";
import type { AppContractRes } from "../_$id/-query/type";
import AppIntegrationContractSummary from "./AppIntegrationContractSummary";

interface AppIntegrationHeaderProps {
  integratedApp: AppContractRes["app"];
  integratedTenantName: string;
  contract: AppContractRes["appContract"];
  isSubordinate: boolean;
}

export default function AppIntegrationHeader({
  integratedApp,
  integratedTenantName,
  contract,
  isSubordinate,
}: AppIntegrationHeaderProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.profile.app.header",
  });

  const getAppLinkProps = (appId: string) => {
    return appId === APP_ID.CUSTOM_APP
      ? {
          to: "/software/custom-saas/add/$customSaasType" as const,
          params: { customSaasType: CustomSaasType.CUSTOM_SAAS },
          hash: "basic" as const,
        }
      : {
          to: "/software/marketplace/$id" as const,
          params: { id: appId },
        };
  };

  const renderAppBadge = (appId: string) => {
    if (appId === APP_ID.CUSTOM_APP) {
      return <Badge variant="secondary">{t("customApp")}</Badge>;
    }
    return null;
  };

  return (
    <HStack className="gap-4">
      <Avatar className="flex h-[60px] w-[60px] items-center justify-center rounded-lg border p-2">
        <AvatarImage src={integratedApp.avatarUrl} alt={integratedApp.name} />
        <AvatarFallback>
          <Icon
            iconName="AppsIcon"
            className="size-full fill-muted-foreground"
          />
        </AvatarFallback>
      </Avatar>
      <VStack>
        <HStack className="gap-3 items-center">
          <Text variant={TextVariant.h2}>{integratedApp.name}</Text>
          <Link {...getAppLinkProps(integratedApp.appId)}>
            <Icon iconName="SquareArrowOutUpRightIcon" />
          </Link>
        </HStack>
        <HStack className="gap-2 items-center">
          <HStack className="gap-2 items-center">
            {renderAppBadge(integratedApp.appId)}
            <Text
              variant={TextVariant.labelSmall}
              className="text-muted-foreground"
            >
              {t("in")}
            </Text>
            <HStack className="items-center gap-1 px-1.5 py-0.5">
              <Icon iconName="NetworkIcon" size={12} />
              <Text variant={TextVariant.labelSmall} className="color-white">
                {integratedTenantName}
              </Text>
            </HStack>
          </HStack>
        </HStack>
      </VStack>
      {!isSubordinate && <AppIntegrationContractSummary contract={contract} />}
    </HStack>
  );
}
